import React, { useState, useMemo } from 'react';
import { RoadmapProject } from '../types';
import {
  getStatusClass,
  getStatusLabel,
  getProjectQuarterInfo,
  getInitials,
  formatCompactCurrency,
} from '../utils/roadmapUtils';

interface ListViewProps {
  projects: RoadmapProject[];
  onProjectClick: (gid: string) => void;
  searchQuery: string;
}

// ─── Status dot strip ─────────────────────────────────────────────────────────

interface StatusDotsProps {
  projects: RoadmapProject[];
}

const StatusDots: React.FC<StatusDotsProps> = ({ projects }) => {
  const counts = useMemo(() => {
    let inProgress = 0, blocked = 0, delayed = 0, inReview = 0;
    let notStarted = 0, onHold = 0, complete = 0, cancelled = 0;
    projects.forEach(p => {
      const s = (p.status || '').toLowerCase();
      if (s.includes('in progress'))                    inProgress++;
      else if (s.includes('blocked'))                   blocked++;
      else if (s.includes('delayed'))                   delayed++;
      else if (s.includes('in review'))                 inReview++;
      else if (s.includes('not started'))               notStarted++;
      else if (s.includes('on hold'))                   onHold++;
      else if (s.includes('complet') || s === 'done')   complete++;
      else if (s.includes('cancel'))                    cancelled++;
    });
    return { inProgress, blocked, delayed, inReview, notStarted, onHold, complete, cancelled };
  }, [projects]);

  return (
    <div className="program-status-dots" aria-hidden="true">
      {Array.from({ length: counts.inProgress }).map((_, i) => (
        <span key={`ip-${i}`} className="status-dot in-progress" />
      ))}
      {Array.from({ length: counts.blocked }).map((_, i) => (
        <span key={`bl-${i}`} className="status-dot blocked" />
      ))}
      {Array.from({ length: counts.delayed }).map((_, i) => (
        <span key={`dl-${i}`} className="status-dot delayed" />
      ))}
      {Array.from({ length: counts.inReview }).map((_, i) => (
        <span key={`ir-${i}`} className="status-dot in-review" />
      ))}
      {Array.from({ length: counts.notStarted }).map((_, i) => (
        <span key={`ns-${i}`} className="status-dot planned" />
      ))}
      {Array.from({ length: counts.onHold }).map((_, i) => (
        <span key={`oh-${i}`} className="status-dot on-hold" />
      ))}
      {Array.from({ length: counts.complete }).map((_, i) => (
        <span key={`co-${i}`} className="status-dot complete" />
      ))}
      {Array.from({ length: counts.cancelled }).map((_, i) => (
        <span key={`ca-${i}`} className="status-dot cancelled" />
      ))}
    </div>
  );
};

// ─── Highlight matched text ───────────────────────────────────────────────────

const HighlightText: React.FC<{ text: string; query: string }> = ({ text, query }) => {
  if (!query || !text) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="search-highlight">{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  );
};

// ─── Project Card ─────────────────────────────────────────────────────────────

interface ProjectCardProps {
  project: RoadmapProject;
  onClick: () => void;
  searchQuery: string;
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project, onClick, searchQuery }) => {
  const quarterInfo = getProjectQuarterInfo(project);
  const statusClass = getStatusClass(project.status);
  const statusLabel = getStatusLabel(project.status);
  const assigneeInitials = project.assignee ? getInitials(project.assignee.name) : null;

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const hasEntitlement = project.annualized_entitlement > 0;
  const hasHc = project.hc_sde > 0;

  return (
    <div
      className="project-card"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onClick()}
      aria-label={`Open details for ${project.name}`}
    >
      {/* Header: name + status badge */}
      <div className="project-card-header">
        <span className="project-card-name">
          <HighlightText text={project.name} query={searchQuery} />
        </span>
        <span className={`status-badge ${statusClass}`}>
          <span className="status-badge-dot" aria-hidden="true" />
          {statusLabel}
        </span>
      </div>

      {/* Meta row: quarter pill + launch date */}
      <div className="project-card-meta">
        {quarterInfo && (
          <span className="quarter-pill-small">{quarterInfo.key}</span>
        )}
        {project.launch_date && (
          <span className="launch-date-small">{formatDate(project.launch_date)}</span>
        )}
      </div>

      {/* Key deliverable (truncated) */}
      {project.key_deliverables && (
        <p className="project-card-deliverable">
          <HighlightText text={project.key_deliverables} query={searchQuery} />
        </p>
      )}

      {/* Metric chips: entitlement + HC */}
      {(hasEntitlement || hasHc) && (
        <div className="project-card-metrics">
          {hasEntitlement && (
            <span className="project-metric-chip">
              <span className="project-metric-chip-label">Entitlement</span>
              <span className="project-metric-chip-value">
                {formatCompactCurrency(project.annualized_entitlement)}
              </span>
            </span>
          )}
          {hasHc && (
            <span className="project-metric-chip">
              <span className="project-metric-chip-label">HC</span>
              <span className="project-metric-chip-value">{project.hc_sde.toFixed(1)}</span>
            </span>
          )}
        </div>
      )}

      {/* Footer: assignee avatar */}
      <div className="project-card-footer">
        {project.assignee ? (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div className="assignee-avatar" title={project.assignee.name}>
              {assigneeInitials}
            </div>
            <span className="assignee-name-small">{project.assignee.name}</span>
          </div>
        ) : (
          <span className="assignee-name-small" style={{ color: 'var(--sr-text-tertiary)' }}>
            Unassigned
          </span>
        )}
      </div>
    </div>
  );
};

// ─── Program Accordion ────────────────────────────────────────────────────────

interface ProgramAccordionProps {
  programName: string;
  projects: RoadmapProject[];
  onProjectClick: (gid: string) => void;
  searchQuery: string;
  defaultOpen?: boolean;
}

const ProgramAccordion: React.FC<ProgramAccordionProps> = ({
  programName,
  projects,
  onProjectClick,
  searchQuery,
  defaultOpen = false,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="program-accordion">
      {/* Header */}
      <div
        className="program-accordion-header"
        onClick={() => setIsOpen(o => !o)}
        role="button"
        tabIndex={0}
        onKeyDown={e => e.key === 'Enter' && setIsOpen(o => !o)}
        aria-expanded={isOpen}
        aria-controls={`accordion-body-${programName}`}
      >
        <span className={`accordion-chevron${isOpen ? ' open' : ''}`} aria-hidden="true">
          ▶
        </span>
        <span className="program-accordion-name">{programName}</span>
        <StatusDots projects={projects} />
        <span className="program-count-badge">{projects.length}</span>
      </div>

      {/* Body — CSS max-height transition */}
      <div
        id={`accordion-body-${programName}`}
        className={`program-accordion-body${isOpen ? ' open' : ''}`}
        role="region"
        aria-labelledby={`accordion-header-${programName}`}
      >
        <div className="program-projects-grid">
          {projects.map(project => (
            <ProjectCard
              key={project.gid}
              project={project}
              onClick={() => onProjectClick(project.gid)}
              searchQuery={searchQuery}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── ListView ─────────────────────────────────────────────────────────────────

export const ListView: React.FC<ListViewProps> = ({ projects, onProjectClick, searchQuery }) => {
  // Group by program, sorted by project count descending
  const programGroups = useMemo(() => {
    const map = new Map<string, RoadmapProject[]>();
    projects.forEach(p => {
      const key = p.program || 'Unassigned';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(p);
    });
    return Array.from(map.entries())
      .sort((a, b) => b[1].length - a[1].length);
  }, [projects]);

  if (projects.length === 0) {
    return (
      <div className="list-view">
        <div className="empty-state">
          <div className="empty-state-icon">🗂️</div>
          <h3>No projects found</h3>
          <p>
            {searchQuery
              ? `No results for "${searchQuery}". Try a different search.`
              : 'Try adjusting your filters.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="list-view">
      {programGroups.map(([programName, programProjects], index) => (
        <ProgramAccordion
          key={programName}
          programName={programName}
          projects={programProjects}
          onProjectClick={onProjectClick}
          searchQuery={searchQuery}
          defaultOpen={index === 0}
        />
      ))}
    </div>
  );
};

export default ListView;